import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useWaProvider } from "../lib/waProvider";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import DateRangePresets from "../components/DateRangePresets";
import { Bell, CheckCheck, Search, Volume2, VolumeX, Moon } from "lucide-react";
import { isSoundEnabled, setSoundEnabled, playNotifSound } from "../lib/notifSound";
import PickupReminderSettings from "../components/PickupReminderSettings";

const PAGE = 50;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const CHANNEL_LABEL = { push: "Push", email: "Email", whatsapp: "WhatsApp" };

export default function AdminNotifications() {
  const { label: waLabel } = useWaProvider();
  const [tab, setTab] = useState("history");
  const [meta, setMeta] = useState(null); // {events, channels, prefs, admin_phone, push_devices}
  const [prefs, setPrefs] = useState({});
  const [quiet, setQuiet] = useState({ enabled: false, start: "22:00", end: "08:00" });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [sound, setSound] = useState(isSoundEnabled());

  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings/notification-prefs").then((r) => { setMeta(r.data); setPrefs(r.data.prefs); setQuiet(r.data.quiet_hours || quiet); }).catch(() => {});
  }, []);

  const eventMap = useMemo(() => Object.fromEntries((meta?.events || []).map((e) => [e.key, e])), [meta]);

  const load = async (append = false) => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset: append ? rows.length : 0 };
      if (type) params.type = type;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/notifications", { params });
      setRows(append ? [...rows, ...data.notifications] : data.notifications);
      setTotal(data.total); setUnread(data.unread);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(false); }, [type, from, to]);

  const markAll = async () => {
    await api.post("/admin/notifications/read-all");
    toast.success("Tutte le notifiche segnate come lette");
    load(false);
  };

  const togglePref = (evt, ch) => setPrefs((p) => ({ ...p, [evt]: { ...p[evt], [ch]: !p[evt]?.[ch] } }));
  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const { data } = await api.put("/settings/notification-prefs", { prefs, quiet_hours: quiet });
      setPrefs(data.prefs); setQuiet(data.quiet_hours); setMeta((m) => ({ ...m, quiet_now: data.quiet_now }));
      toast.success("Preferenze notifiche salvate");
    }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingPrefs(false); }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="admin-notifications-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="section-label">Console</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold flex items-center gap-2"><Bell className="h-7 w-7 text-amber-300" /> Notifiche admin</h1>
          <p className="text-zinc-400 text-sm">Storico completo e preferenze dei canali (campanella, push, email, WhatsApp).</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setTab("history")} data-testid="notif-tab-history"
            className={`px-3 py-1.5 rounded-full text-sm border ${tab === "history" ? "bg-amber-500/20 text-amber-200 border-amber-500/50" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
            Storico {unread > 0 && <span className="ml-1 text-[10px] bg-[color:var(--paprika)] text-white rounded-full px-1.5">{unread}</span>}
          </button>
          <button type="button" onClick={() => setTab("prefs")} data-testid="notif-tab-prefs"
            className={`px-3 py-1.5 rounded-full text-sm border ${tab === "prefs" ? "bg-amber-500/20 text-amber-200 border-amber-500/50" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
            Preferenze
          </button>
        </div>
      </div>

      {tab === "history" && (
        <div className="card-elev p-4 space-y-3" data-testid="notif-history">
          <div className="flex flex-col lg:flex-row gap-2 lg:items-end">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tipo</label>
              <select data-testid="notif-filter-type" value={type} onChange={(e) => setType(e.target.value)}
                className="h-9 w-full rounded-md bg-zinc-950 border border-zinc-800 text-sm text-zinc-200 px-2">
                <option value="">Tutti i tipi</option>
                {(meta?.events || []).map((e) => <option key={e.key} value={e.key}>{e.icon} {e.label}</option>)}
                <option value="campaign_auto_launch_error">❌ Errore campagna programmata</option>
              </select>
            </div>
            <div className="flex gap-2">
              <div><label className="text-[10px] uppercase tracking-widest text-zinc-500">Dal</label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="notif-date-from" className="bg-zinc-950 border-zinc-800 h-9" /></div>
              <div><label className="text-[10px] uppercase tracking-widest text-zinc-500">Al</label>
                <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} data-testid="notif-date-to" className="bg-zinc-950 border-zinc-800 h-9" /></div>
            </div>
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Cerca</label>
              <div className="flex gap-1">
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(false)} placeholder="Titolo o testo…" data-testid="notif-search" className="bg-zinc-950 border-zinc-800 h-9" />
                <Button size="sm" variant="outline" className="h-9 border-zinc-800" onClick={() => load(false)} data-testid="notif-search-btn"><Search className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <DateRangePresets from={from} to={to} onChange={({ from: f, to: t }) => { setFrom(f); setTo(t); }} testId="notif-date-preset" />
            <div className="flex items-center gap-3">
              <span className="text-xs text-zinc-500" data-testid="notif-result-counter">{total} notifiche</span>
              {unread > 0 && <button type="button" onClick={markAll} data-testid="notif-mark-all" className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" /> Segna tutte lette</button>}
            </div>
          </div>

          <ul className="divide-y divide-zinc-800/60" data-testid="notif-list">
            {rows.length === 0 && !loading && <li className="py-8 text-center text-sm text-zinc-500">Nessuna notifica con questi filtri.</li>}
            {rows.map((n) => (
              <li key={n.id} data-testid={`notif-row-${n.id}`} className={`py-2.5 px-2 flex items-start gap-3 text-sm ${n.read_at ? "text-zinc-400" : "bg-indigo-500/5 text-zinc-100"}`}>
                <span className="text-lg leading-none">{eventMap[n.type]?.icon || (n.type === "campaign_auto_launch_error" ? "❌" : "🔔")}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{n.title}</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-800 rounded px-1">{eventMap[n.type]?.label || n.type}</span>
                    {!n.read_at && <span className="h-2 w-2 rounded-full bg-indigo-400" />}
                  </div>
                  {n.body && <div className="text-xs opacity-80 mt-0.5">{n.body}</div>}
                  <div className="text-[10px] opacity-60 mt-0.5">{fmt(n.created_at)}{n.url && <a href={n.url} className="ml-2 text-indigo-300 hover:underline">apri →</a>}</div>
                </div>
              </li>
            ))}
          </ul>
          {rows.length < total && (
            <div className="text-center"><Button variant="outline" size="sm" className="border-zinc-800" onClick={() => load(true)} disabled={loading} data-testid="notif-load-more">Carica altre ({total - rows.length})</Button></div>
          )}
        </div>
      )}

      {tab === "prefs" && meta && (
        <div className="card-elev p-4 space-y-4" data-testid="notif-prefs">
          <PickupReminderSettings />
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-500">
            <span>La campanella riceve sempre tutto. Scegli per ogni evento i canali aggiuntivi.
              {" "}Push attive su <b className="text-zinc-300">{meta.push_devices}</b> dispositivi · WhatsApp admin: <b className="text-zinc-300">{meta.admin_phone || "non impostato (Statistiche → Alert KPI)"}</b>.</span>
            <label className="flex items-center gap-2 text-zinc-300" data-testid="notif-sound-toggle">
              {sound ? <Volume2 className="h-4 w-4 text-emerald-300" /> : <VolumeX className="h-4 w-4" />}
              Suono in console
              <Switch checked={sound} onCheckedChange={(v) => { setSound(v); setSoundEnabled(v); if (v) playNotifSound(); }} data-testid="notif-sound-switch" />
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="notif-prefs-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="text-left py-2 pr-2">Evento</th>
                  <th className="py-2 text-center">Campanella</th>
                  {meta.channels.map((c) => <th key={c} className="py-2 text-center">{CHANNEL_LABEL[c]}</th>)}
                </tr>
              </thead>
              <tbody>
                {meta.events.map((e) => (
                  <tr key={e.key} className="border-b border-zinc-800/60" data-testid={`notif-pref-row-${e.key}`}>
                    <td className="py-2 pr-2 text-zinc-200">{e.icon} {e.label}</td>
                    <td className="py-2 text-center text-emerald-300">✓</td>
                    {meta.channels.map((c) => (
                      <td key={c} className="py-2 text-center">
                        <Switch checked={!!prefs[e.key]?.[c]} onCheckedChange={() => togglePref(e.key, c)} data-testid={`notif-pref-${e.key}-${c}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 space-y-2" data-testid="notif-quiet-hours">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-zinc-200"><Moon className="h-4 w-4 text-indigo-300" /> Non disturbare (fascia notturna)
                {meta.quiet_now && <span data-testid="notif-quiet-now" className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-700/50">attiva ora</span>}
              </div>
              <Switch checked={!!quiet.enabled} onCheckedChange={(v) => setQuiet((q) => ({ ...q, enabled: v }))} data-testid="notif-quiet-switch" />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
              <label className="flex items-center gap-1.5">Dalle
                <input type="time" value={quiet.start} onChange={(e) => setQuiet((q) => ({ ...q, start: e.target.value }))} disabled={!quiet.enabled} data-testid="notif-quiet-start"
                  className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-200 disabled:opacity-40" />
              </label>
              <label className="flex items-center gap-1.5">alle
                <input type="time" value={quiet.end} onChange={(e) => setQuiet((q) => ({ ...q, end: e.target.value }))} disabled={!quiet.enabled} data-testid="notif-quiet-end"
                  className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-200 disabled:opacity-40" />
              </label>
              <span>(ora italiana) · push e suoni silenziati per tutti gli operatori; la campanella, email e WhatsApp non cambiano.</span>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={savePrefs} disabled={savingPrefs} data-testid="notif-prefs-save" className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">Salva preferenze</Button>
          </div>
          <p className="text-[11px] text-zinc-500">Il canale WhatsApp per ordini/messaggi/prenotazioni usa il template {waLabel} <code>admin_alert</code> (variabili <code>title</code>, <code>body</code>); calo/picco KPI e riepilogo usano le rispettive automazioni.</p>
        </div>
      )}
    </div>
  );
}
